import React, { useCallback, useEffect, useRef, useState } from 'react';
import { expoRequest } from '@/api/expo';
import { freshExpoPack, seatPackFingerprint, expoVarietyNotice } from '@/state/expo';
import { useNoReferrer } from '@/components/Class/useNoReferrer';
import './Expo.css';

const read = (key) => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } };
export function ExpoStation({ eventKey, renderEditor }) {
  useNoReferrer();
  const storeKey = `ba-expo:${eventKey}`;
  const stationCaption = (data) => data?.station_name ? `${data.name} · ${data.station_name}` : data?.name;
  const [config, setConfig] = useState(null);
  const [visitor, setVisitor] = useState(() => {
    const saved = read(storeKey);
    return saved?.expires_at > new Date().toISOString() ? saved : null;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [nextReady, setNextReady] = useState(false);
  const [varietyNotice, setVarietyNotice] = useState('');
  const pending = useRef(null);
  const prepared = useRef(null);
  const history = useRef(visitor?.history || []);
  const current = useRef(visitor);
  const running = useRef(false);
  current.current = visitor;
  const prepare = (data) => ({
    request_id: crypto.randomUUID(),
    pack: freshExpoPack(data.packs, data.pack_history || history.current, {previousPack:current.current?.pack}),
    ...(data.pack_revision != null ? {pack_revision:data.pack_revision} : {}),
  });
  useEffect(() => {
    let active = true;
    setNextReady(false);
    expoRequest(`/station/${encodeURIComponent(eventKey)}`).then((data) => {
      if (!active) return;
      const nextPack = prepare(data);
      setConfig(data); prepared.current=nextPack; setNextReady(true);
      setVarietyNotice(expoVarietyNotice(data.packs)); setError('');
    }).catch((e) => {if(active) setError(e.message);});
    return () => { active = false; };
    // Each acknowledged visitor refreshes shared usage before preloading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventKey, retry, visitor?.token]);
  const persist = useCallback((patch) => {
    if (!current.current) return;
    const updated = { ...current.current, ...patch };
    current.current = updated;
    try { sessionStorage.setItem(storeKey, JSON.stringify(updated)); } catch { /* The in-memory beat remains usable. */ }
  }, [storeKey]);
  const onSnapshot = useCallback((snapshot) => persist({ snapshot }), [persist]);
  const next = async () => {
    if (running.current || !config) return;
    running.current = true; setBusy(true); setError('');
    try {
      // Keep request identity and the chosen pack across network retries. The old editor stays
      // mounted until the server acknowledges the new visitor; no beat is lost on a failed reset.
      if (!pending.current) pending.current = prepared.current || prepare(config);
      let result;
      for (let attempt=0;attempt<4;attempt++) {
        try {
          result=await expoRequest(`/station/${encodeURIComponent(eventKey)}/start`, {body:pending.current,method:'POST'});
          break;
        } catch(e) {
          if(e.code !== 'expo_pack_stale') throw e;
          // An explicit stale response confirms this request did not create a visitor.
          // Network errors never discard its identity or chosen pack.
          pending.current=null; prepared.current=null; setNextReady(false);
          const latest=await expoRequest(`/station/${encodeURIComponent(eventKey)}`);
          setConfig(latest);pending.current=prepare(latest);
        }
      }
      if(!result) throw new Error('Other stations are starting beats. Please try Next kid again. Your current beat is safe.');
      const nextHistory = [...history.current, seatPackFingerprint(pending.current.pack)].slice(-64);
      const value = { ...result, pack: pending.current.pack, history: nextHistory };
      try { sessionStorage.setItem(storeKey, JSON.stringify(value)); } catch { /* Session memory still works. */ }
      history.current = nextHistory; current.current = value; setVisitor(value);
      pending.current = null; prepared.current = null; setNextReady(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); running.current = false; }
  };
  const exportBeat = async (blob) => {
    const body = new FormData(); body.append('mp3', blob, 'beat.mp3');
    const result = await expoRequest('/beat', { token: current.current.token, body, method: 'POST' });
    persist({ handoff: result });
    return result;
  };
  if (!config) return <main className="expo-page" data-skin="playground"><img src="/Pizzul_Hero.png" alt="Pizzul" /><h1>Expo station</h1><p role="status">{error || 'Loading the Expo sounds...'}</p>{error && <button onClick={() => setRetry((v) => v + 1)}>Retry</button>}</main>;
  if (!visitor) return <main className="expo-page" data-skin="playground"><img src="/Pizzul_Hero.png" alt="Pizzul" /><h1>{stationCaption(config)}</h1><p>A soundpack is ready. No name, email or sign-in needed.</p><button onClick={next} disabled={busy || (!nextReady && !error)}>{busy ? 'Opening soundpack...' : 'Start making a beat'}</button>{varietyNotice && <p role="status">{varietyNotice}</p>}{error && <p role="alert">{error}</p>}</main>;
  return renderEditor({ key: visitor.token, classPack: visitor.pack, kiosk: true,
    expoMode: { eventName: stationCaption(config), initialSnapshot: visitor.snapshot, initialHandoff: visitor.handoff,
      onSnapshot, onNext: next, onExport: exportBeat, busy, nextReady, error, varietyNotice } });
}
